import React from "react";
import {Input, InputNumber, Select, Tooltip, Popconfirm} from "antd";
import {PlusOutlined, DeleteOutlined, InfoCircleOutlined} from "@ant-design/icons";
import {CustomTimeFeeItem} from "./CustomTimeFeeItem";
import {paymentContractFeeTypesCompare, daysList2} from "constants/constants";
import config from "_core/config";
import {
    FeeTypeCard as StyledFeeTypeCard,
    FeeTypeHeader,
    FeeTypeTitle,
    FeeTypeBody,
    FormItem,
    FormLabel,
    FormInputRow,
    CustomTimeSection,
    CustomTimeHeader,
    CustomTimeTitle,
    CustomTimeList,
    AddButton,
    RemoveButton
} from "../SubCompanyPaymentContractForm.styles";

const KDV_RATE = config.KDV;

// KDV bilgisi tooltip içeriği oluşturan helper
const getKdvTooltipContent = (amount, isClientAmount = true) => {
    if (!amount || amount === 0) return null;
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return null;

    // Müşteri ücretleri KDV hariç girilir, Kurye ücretleri KDV dahil girilir
    if (isClientAmount) {
        const includeVAT = numAmount * (1 + KDV_RATE);
        return (
            <div>
                <div>KDV Hariç Ücret: {numAmount.toFixed(2)} TL</div>
                <div>KDV Dahil Ücret: {includeVAT.toFixed(2)} TL</div>
                <div style={{marginTop: 4, color: "#faad14"}}>
                    KDV: %{(KDV_RATE * 100).toFixed(0)}
                </div>
            </div>
        );
    } else {
        const excludeVAT = numAmount / (1 + KDV_RATE);
        return (
            <div>
                <div>KDV Dahil Ücret: {numAmount.toFixed(2)} TL</div>
                <div>KDV Hariç Ücret: {excludeVAT.toFixed(2)} TL</div>
                <div style={{marginTop: 4, color: "#faad14"}}>
                    KDV: %{(KDV_RATE * 100).toFixed(0)}
                </div>
            </div>
        );
    }
};

// Saatlik Paket Garanti için tooltip içeriği (form düzenleme modu - hesaplama yapar)
const getPackageGuaranteeTooltipContent = (packageCount, packagePrice, isClientAmount = true) => {
    if (!packageCount || !packagePrice) return null;
    const numCount = parseFloat(packageCount);
    const numPrice = parseFloat(packagePrice);
    if (isNaN(numCount) || isNaN(numPrice)) return null;

    const total = numCount * numPrice;

    // Müşteri ücretleri KDV hariç girilir, Kurye ücretleri KDV dahil girilir
    if (isClientAmount) {
        const totalIncludeVAT = total * (1 + KDV_RATE);
        return (
            <div>
                <div>Paket Sayısı: {numCount}</div>
                <div>Paket Ücreti: {numPrice.toFixed(2)} TL</div>
                <div
                    style={{
                        borderTop: "1px solid rgba(255,255,255,0.3)",
                        marginTop: 4,
                        paddingTop: 4
                    }}
                >
                    <div>Toplam (KDV Hariç): {total.toFixed(2)} TL</div>
                    <div>Toplam (KDV Dahil): {totalIncludeVAT.toFixed(2)} TL</div>
                    <div style={{marginTop: 4, color: "#faad14"}}>
                        KDV: %{(KDV_RATE * 100).toFixed(0)}
                    </div>
                </div>
            </div>
        );
    } else {
        const totalExcludeVAT = total / (1 + KDV_RATE);
        return (
            <div>
                <div>Paket Sayısı: {numCount}</div>
                <div>Paket Ücreti: {numPrice.toFixed(2)} TL</div>
                <div
                    style={{
                        borderTop: "1px solid rgba(255,255,255,0.3)",
                        marginTop: 4,
                        paddingTop: 4
                    }}
                >
                    <div>Toplam (KDV Dahil): {total.toFixed(2)} TL</div>
                    <div>Toplam (KDV Hariç): {totalExcludeVAT.toFixed(2)} TL</div>
                    <div style={{marginTop: 4, color: "#faad14"}}>
                        KDV: %{(KDV_RATE * 100).toFixed(0)}
                    </div>
                </div>
            </div>
        );
    }
};

// Saatlik Paket Garanti için tooltip içeriği (detay modu - BE'den gelen hesaplanmış değer)
const getPackageGuaranteeDetailTooltipContent = (
    packageCount,
    totalAmount,
    isClientAmount = true
) => {
    if (!packageCount || !totalAmount) return null;
    const numCount = parseFloat(packageCount);
    const numTotal = parseFloat(totalAmount);
    if (isNaN(numCount) || isNaN(numTotal)) return null;

    // Müşteri ücretleri KDV hariç, Kurye ücretleri KDV dahil
    if (isClientAmount) {
        const totalIncludeVAT = numTotal * (1 + KDV_RATE);
        return (
            <div>
                <div>Paket Sayısı: {numCount}</div>
                <div
                    style={{
                        borderTop: "1px solid rgba(255,255,255,0.3)",
                        marginTop: 4,
                        paddingTop: 4
                    }}
                >
                    <div>Toplam (KDV Hariç): {numTotal.toFixed(2)} TL</div>
                    <div>Toplam (KDV Dahil): {totalIncludeVAT.toFixed(2)} TL</div>
                    <div style={{marginTop: 4, color: "#faad14"}}>
                        KDV: %{(KDV_RATE * 100).toFixed(0)}
                    </div>
                </div>
            </div>
        );
    } else {
        const totalExcludeVAT = numTotal / (1 + KDV_RATE);
        return (
            <div>
                <div>Paket Sayısı: {numCount}</div>
                <div
                    style={{
                        borderTop: "1px solid rgba(255,255,255,0.3)",
                        marginTop: 4,
                        paddingTop: 4
                    }}
                >
                    <div>Toplam (KDV Dahil): {numTotal.toFixed(2)} TL</div>
                    <div>Toplam (KDV Hariç): {totalExcludeVAT.toFixed(2)} TL</div>
                    <div style={{marginTop: 4, color: "#faad14"}}>
                        KDV: %{(KDV_RATE * 100).toFixed(0)}
                    </div>
                </div>
            </div>
        );
    }
};

export const FeeTypeCard = ({
    feeType,
    feeTypeName,
    defaultFeeItem,
    customFeeItems,
    disabled,
    onDefaultChange,
    onCustomItemChange,
    onAddCustomItem,
    onRemoveCustomItem,
    onRemoveFeeType,
    userIsCustomer,
    isPool = false,
    packagePriceInfo = null,
    isEditing = false
}) => {
    const handleDefaultFieldChange = (field, value) => {
        onDefaultChange(feeType, field, value);
    };

    const handleDefaultOptionsChange = (field, value) => {
        onDefaultChange(feeType, `options.${field}`, value);
    };

    const handleCustomItemChange = (index, field, value) => {
        onCustomItemChange(feeType, index, field, value);
    };

    const handleRemoveCustomItem = index => {
        onRemoveCustomItem(feeType, index);
    };

    const handleAddCustomItem = () => {
        onAddCustomItem(feeType);
    };

    const getSelectedDays = days => {
        if (!days) return [];
        return Object.keys(days)
            .filter(key => days[key] === true || days[key] === "true")
            .map(key => {
                const day = daysList2.find(d => d.value.toString() === key);
                return day?.name || "";
            })
            .filter(Boolean);
    };

    // Check if this fee type needs special fields in default item
    const needsFeeTypesCompare = feeType === "SAATLIK_GARANTI_PAKET";
    const needsCourierMinimumDistance = feeType === "KM";
    const needsClientMinimumDistance = feeType === "KM";
    const isPackageGuarantee = feeType === "SAATLIK_GARANTI_PAKET";
    const isPackageType = feeType === "PAKET";
    const isBonusPackage = feeType === "BONUS_PAKET_KAZANCI_UCRETI";

    // BONUS_PAKET için tüm satırlar customFeeItems içinde gösterilecek, default bölümü yok
    const showDefaultSection = !isBonusPackage;

    // PAKET için kademe gösterilecek (pool hariç), diğerleri için özel gün
    const showCustomSection =
        defaultFeeItem !== undefined || (isBonusPackage && customFeeItems?.length > 0);

    // BONUS_PAKET için başlık gösterilmeyecek
    const customSectionTitle = isBonusPackage
        ? null
        : isPackageType
        ? "Kademeli Paket Tanımlamaları"
        : "Özel Gün Tanımlamaları";

    // BONUS_PAKET için "Yeni Gün Ekle" yazacak
    const addButtonText = isBonusPackage
        ? "Yeni Gün Ekle"
        : isPackageType
        ? "Kademe Ekle"
        : "Özel Gün Ekle";

    // Pool firmalarında PAKET için kademe gösterilmeyecek
    const showCustomTimeSection = showCustomSection && !(isPackageType && isPool);

    return (
        <StyledFeeTypeCard>
            <FeeTypeHeader>
                <FeeTypeTitle>{feeTypeName}</FeeTypeTitle>
                {!disabled && onRemoveFeeType && (
                    <Popconfirm
                        title="Bu ücret tipini kaldırmak istediğinize emin misiniz?"
                        onConfirm={() => onRemoveFeeType(feeType)}
                        okText="Evet"
                        cancelText="Hayır"
                    >
                        <DeleteOutlined
                            style={{
                                fontSize: 16,
                                color: "#ff4d4f",
                                cursor: "pointer",
                                transition: "all 0.3s"
                            }}
                            onMouseEnter={e => (e.currentTarget.style.color = "#ff7875")}
                            onMouseLeave={e => (e.currentTarget.style.color = "#ff4d4f")}
                        />
                    </Popconfirm>
                )}
            </FeeTypeHeader>

            <FeeTypeBody>
                {/* BONUS_PAKET için default bölümü gösterilmez, tüm satırlar custom olarak işlenir */}
                {showDefaultSection && (
                    <FormInputRow style={{flexWrap: "wrap", gap: 8}}>
                        {/* SAATLIK_GARANTI_PAKET için Paket Sayısı alanları */}
                        {isPackageGuarantee ? (
                            <>
                                <FormItem style={{minWidth: 130}}>
                                    <FormLabel
                                        style={{display: "flex", alignItems: "center", gap: 4}}
                                    >
                                        {userIsCustomer ? "Paket Sayısı" : "Müşteri Paket Sayısı"}
                                        {/* Form modunda hesaplama yapan tooltip */}
                                        {isEditing ? (
                                            defaultFeeItem?.clientGuaranteePackageCount &&
                                            packagePriceInfo?.clientAmount ? (
                                                <Tooltip
                                                    title={getPackageGuaranteeTooltipContent(
                                                        defaultFeeItem.clientGuaranteePackageCount,
                                                        packagePriceInfo.clientAmount,
                                                        true
                                                    )}
                                                >
                                                    <InfoCircleOutlined
                                                        style={{
                                                            color: "#1890ff",
                                                            cursor: "pointer"
                                                        }}
                                                    />
                                                </Tooltip>
                                            ) : (
                                                <InfoCircleOutlined />
                                            )
                                        ) : defaultFeeItem?.clientGuaranteePackageCount &&
                                          !packagePriceInfo &&
                                          defaultFeeItem?.clientGuaranteePackageAmount ? (
                                            <Tooltip
                                                title={getPackageGuaranteeDetailTooltipContent(
                                                    defaultFeeItem.clientGuaranteePackageCount,
                                                    defaultFeeItem.clientGuaranteePackageAmount,
                                                    true
                                                )}
                                            >
                                                <InfoCircleOutlined
                                                    style={{color: "#1890ff", cursor: "pointer"}}
                                                />
                                            </Tooltip>
                                        ) : (
                                            <InfoCircleOutlined />
                                        )}
                                    </FormLabel>
                                    <InputNumber
                                        className="payment-contract-form-input"
                                        size="small"
                                        value={defaultFeeItem?.clientGuaranteePackageCount ?? ""}
                                        onChange={value =>
                                            handleDefaultFieldChange(
                                                "clientGuaranteePackageCount",
                                                Math.abs(value)
                                            )
                                        }
                                        disabled={disabled}
                                        min={0}
                                        style={{width: "100%"}}
                                    />
                                </FormItem>

                                {!userIsCustomer && (
                                    <FormItem style={{minWidth: 130}}>
                                        <FormLabel
                                            style={{display: "flex", alignItems: "center", gap: 4}}
                                        >
                                            Kurye Paket Sayısı
                                            {isEditing ? (
                                                defaultFeeItem?.courierGuaranteePackageCount &&
                                                packagePriceInfo?.courierAmount ? (
                                                    <Tooltip
                                                        title={getPackageGuaranteeTooltipContent(
                                                            defaultFeeItem.courierGuaranteePackageCount,
                                                            packagePriceInfo.courierAmount,
                                                            false
                                                        )}
                                                    >
                                                        <InfoCircleOutlined
                                                            style={{
                                                                color: "#1890ff",
                                                                cursor: "pointer"
                                                            }}
                                                        />
                                                    </Tooltip>
                                                ) : (
                                                    <InfoCircleOutlined />
                                                )
                                            ) : defaultFeeItem?.courierGuaranteePackageCount &&
                                              !packagePriceInfo &&
                                              defaultFeeItem?.courierGuaranteePackageAmount ? (
                                                <Tooltip
                                                    title={getPackageGuaranteeDetailTooltipContent(
                                                        defaultFeeItem.courierGuaranteePackageCount,
                                                        defaultFeeItem.courierGuaranteePackageAmount,
                                                        false
                                                    )}
                                                >
                                                    <InfoCircleOutlined
                                                        style={{
                                                            color: "#1890ff",
                                                            cursor: "pointer"
                                                        }}
                                                    />
                                                </Tooltip>
                                            ) : (
                                                <InfoCircleOutlined />
                                            )}
                                        </FormLabel>
                                        <InputNumber
                                            className="payment-contract-form-input"
                                            size="small"
                                            value={
                                                defaultFeeItem?.courierGuaranteePackageCount ?? ""
                                            }
                                            onChange={value =>
                                                handleDefaultFieldChange(
                                                    "courierGuaranteePackageCount",
                                                    Math.abs(value)
                                                )
                                            }
                                            disabled={disabled}
                                            min={0}
                                            style={{width: "100%"}}
                                        />
                                    </FormItem>
                                )}
                            </>
                        ) : (
                            <>
                                {/* Diğer ücret tipleri için standart Müşteri/Kurye Ücreti alanları */}
                                <FormItem style={{minWidth: 90}}>
                                    <FormLabel
                                        style={{display: "flex", alignItems: "center", gap: 4}}
                                    >
                                        {userIsCustomer ? "Ücret" : "Müşteri Ücreti"}
                                        {defaultFeeItem?.clientAmount ? (
                                            <Tooltip
                                                title={getKdvTooltipContent(
                                                    defaultFeeItem.clientAmount,
                                                    true
                                                )}
                                            >
                                                <InfoCircleOutlined
                                                    style={{color: "#1890ff", cursor: "pointer"}}
                                                />
                                            </Tooltip>
                                        ) : (
                                            <InfoCircleOutlined />
                                        )}
                                    </FormLabel>
                                    <InputNumber
                                        className="payment-contract-form-input"
                                        type="number"
                                        size="small"
                                        value={defaultFeeItem?.clientAmount ?? ""}
                                        onChange={e =>
                                            handleDefaultFieldChange("clientAmount", Math.abs(e))
                                        }
                                        disabled={disabled}
                                        min={0}
                                    />
                                </FormItem>

                                {!userIsCustomer && (
                                    <FormItem style={{minWidth: 90}}>
                                        <FormLabel
                                            style={{display: "flex", alignItems: "center", gap: 4}}
                                        >
                                            Kurye Ücreti
                                            {defaultFeeItem?.courierAmount ? (
                                                <Tooltip
                                                    title={getKdvTooltipContent(
                                                        defaultFeeItem.courierAmount,
                                                        false
                                                    )}
                                                >
                                                    <InfoCircleOutlined
                                                        style={{
                                                            color: "#1890ff",
                                                            cursor: "pointer"
                                                        }}
                                                    />
                                                </Tooltip>
                                            ) : (
                                                <InfoCircleOutlined />
                                            )}
                                        </FormLabel>
                                        <InputNumber
                                            className="payment-contract-form-input"
                                            type="number"
                                            size="small"
                                            value={defaultFeeItem?.courierAmount ?? ""}
                                            onChange={e =>
                                                handleDefaultFieldChange(
                                                    "courierAmount",
                                                    Math.abs(e)
                                                )
                                            }
                                            disabled={disabled}
                                            min={0}
                                        />
                                    </FormItem>
                                )}
                            </>
                        )}

                        {!userIsCustomer && needsCourierMinimumDistance && (
                            <FormItem style={{minWidth: 110}}>
                                <FormLabel>Kurye Minimum Mesafe (km)</FormLabel>
                                <InputNumber
                                    className="payment-contract-form-input"
                                    type="number"
                                    size="small"
                                    value={defaultFeeItem?.courierMinimumDistance ?? ""}
                                    onChange={e =>
                                        handleDefaultFieldChange("courierMinimumDistance", Math.abs(e))
                                    }
                                    disabled={disabled}
                                    min={0}
                                />
                            </FormItem>
                        )}


                        {needsClientMinimumDistance && (
                            <FormItem style={{minWidth: 110}}>
                                <FormLabel>Müşteri Minimum Mesafe (km)</FormLabel>
                                <InputNumber
                                    className="payment-contract-form-input"
                                    type="number"
                                    size="small"
                                    value={defaultFeeItem?.clientMinimumDistance ?? ""}
                                    onChange={e =>
                                        handleDefaultFieldChange("clientMinimumDistance", Math.abs(e))
                                    }
                                    disabled={disabled}
                                    min={0}
                                />
                            </FormItem>
                        )}
                        {needsFeeTypesCompare && (
                            <FormItem style={{minWidth: 170}}>
                                <FormLabel>Karşılaştırılacak Ücret Tipleri</FormLabel>
                                <Select
                                    className="payment-contract-form-input"
                                    mode="multiple"
                                    size="small"
                                    value={defaultFeeItem?.options?.feeTypesCompare || []}
                                    onChange={value =>
                                        handleDefaultOptionsChange("feeTypesCompare", value)
                                    }
                                    disabled={disabled}
                                    placeholder="Seçiniz"
                                    style={{width: "100%"}}
                                >
                                    {paymentContractFeeTypesCompare.map(type => (
                                        <Select.Option key={type.value} value={type.value}>
                                            {type.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </FormItem>
                        )}
                    </FormInputRow>
                )}

                {/* BONUS_PAKET için tüm satırlar default görünümde gösterilir */}
                {isBonusPackage && customFeeItems?.length > 0 && (
                    <>
                        {customFeeItems.map((feeItem, index) => {
                            const selectedDaysText =
                                getSelectedDays(feeItem.options?.days).join(", ") ||
                                "Gün seçilmedi";
                            const canDelete = customFeeItems.length > 1;

                            return (
                                <FormInputRow
                                    key={index}
                                    style={{
                                        flexWrap: "wrap",
                                        gap: 8,
                                        marginTop: 0,
                                        marginBottom: 0,
                                        paddingTop: index > 0 ? 2 : 0,
                                        paddingBottom: 12,
                                        borderBottom:
                                            index < customFeeItems.length - 1
                                                ? "1px solid #f0f0f0"
                                                : "none"
                                    }}
                                >
                                    <FormItem style={{minWidth: 90}}>
                                        <FormLabel
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 4
                                            }}
                                        >
                                            {userIsCustomer ? "Ücret" : "Müşteri Ücreti"}
                                            {feeItem.clientAmount ? (
                                                <Tooltip
                                                    title={getKdvTooltipContent(
                                                        feeItem.clientAmount,
                                                        true
                                                    )}
                                                >
                                                    <InfoCircleOutlined
                                                        style={{
                                                            color: "#1890ff",
                                                            cursor: "pointer"
                                                        }}
                                                    />
                                                </Tooltip>
                                            ) : (
                                                <InfoCircleOutlined />
                                            )}
                                        </FormLabel>
                                        <InputNumber
                                            className="payment-contract-form-input"
                                            type="number"
                                            size="small"
                                            value={feeItem.clientAmount ?? ""}
                                            onChange={e =>
                                                handleCustomItemChange(
                                                    index,
                                                    "clientAmount",
                                                    Math.abs(e)
                                                )
                                            }
                                            disabled={disabled}
                                            min={0}
                                        />
                                    </FormItem>

                                    {!userIsCustomer && (
                                        <FormItem style={{minWidth: 90}}>
                                            <FormLabel
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 4
                                                }}
                                            >
                                                Kurye Ücreti
                                                {feeItem.courierAmount ? (
                                                    <Tooltip
                                                        title={getKdvTooltipContent(
                                                            feeItem.courierAmount,
                                                            false
                                                        )}
                                                    >
                                                        <InfoCircleOutlined
                                                            style={{
                                                                color: "#1890ff",
                                                                cursor: "pointer"
                                                            }}
                                                        />
                                                    </Tooltip>
                                                ) : (
                                                    <InfoCircleOutlined />
                                                )}
                                            </FormLabel>
                                            <InputNumber
                                                className="payment-contract-form-input"
                                                type="number"
                                                size="small"
                                                value={feeItem.courierAmount ?? ""}
                                                onChange={e =>
                                                    handleCustomItemChange(
                                                        index,
                                                        "courierAmount",
                                                        Math.abs(e)
                                                    )
                                                }
                                                disabled={disabled}
                                                min={0}
                                            />
                                        </FormItem>
                                    )}

                                    <FormItem style={{minWidth: 110}}>
                                        <FormLabel>Minimum Paket Adedi</FormLabel>
                                        <InputNumber
                                            className="payment-contract-form-input"
                                            type="number"
                                            size="small"
                                            value={feeItem.options?.minPackageCount ?? ""}
                                            onChange={e =>
                                                handleCustomItemChange(
                                                    index,
                                                    "options.minPackageCount",
                                                    Math.abs(e)
                                                )
                                            }
                                            disabled={disabled}
                                            min={0}
                                        />
                                    </FormItem>

                                    <FormItem style={{minWidth: 180}}>
                                        <FormLabel>Günler</FormLabel>
                                        <Tooltip title={selectedDaysText}>
                                            <Select
                                                className="payment-contract-form-input"
                                                mode="multiple"
                                                size="small"
                                                value={getSelectedDays(feeItem.options?.days)}
                                                onChange={selectedDays => {
                                                    const newDays = {
                                                        "0": false,
                                                        "1": false,
                                                        "2": false,
                                                        "3": false,
                                                        "4": false,
                                                        "5": false,
                                                        "6": false
                                                    };
                                                    selectedDays.forEach(dayName => {
                                                        const day = daysList2.find(
                                                            d => d.name === dayName
                                                        );
                                                        if (day) {
                                                            newDays[day.value.toString()] = true;
                                                        }
                                                    });
                                                    handleCustomItemChange(
                                                        index,
                                                        "options.days",
                                                        newDays
                                                    );
                                                }}
                                                disabled={disabled}
                                                placeholder="Günleri seçiniz"
                                                style={{width: "100%"}}
                                            >
                                                {daysList2.map(day => (
                                                    <Select.Option key={day.value} value={day.name}>
                                                        {day.name}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Tooltip>
                                    </FormItem>

                                    {/* Silme butonu - satırın en sağında */}
                                    {!disabled && canDelete && (
                                        <div
                                            style={{
                                                marginLeft: "auto",
                                                display: "flex",
                                                alignItems: "flex-end",
                                                paddingBottom: 4
                                            }}
                                        >
                                            <Tooltip title="Satırı Sil">
                                                <RemoveButton
                                                    onClick={() => handleRemoveCustomItem(index)}
                                                >
                                                    <DeleteOutlined style={{fontSize: 12}} />
                                                </RemoveButton>
                                            </Tooltip>
                                        </div>
                                    )}
                                </FormInputRow>
                            );
                        })}

                        {!disabled && (
                            <AddButton onClick={handleAddCustomItem}>
                                <PlusOutlined />
                                {addButtonText}
                            </AddButton>
                        )}
                    </>
                )}

                {/* Diğer ücret tipleri için custom time section */}
                {showCustomTimeSection && !isBonusPackage && (
                    <CustomTimeSection>
                        {customSectionTitle && (
                            <CustomTimeHeader>
                                <CustomTimeTitle>{customSectionTitle}</CustomTimeTitle>
                            </CustomTimeHeader>
                        )}

                        <CustomTimeList>
                            {customFeeItems?.map((feeItem, index) => (
                                <CustomTimeFeeItem
                                    key={index}
                                    feeItem={feeItem}
                                    feeType={feeType}
                                    index={index}
                                    disabled={disabled}
                                    onRemove={handleRemoveCustomItem}
                                    onChange={handleCustomItemChange}
                                    userIsCustomer={userIsCustomer}
                                    packagePriceInfo={packagePriceInfo}
                                    isEditing={isEditing}
                                    canDelete={true}
                                />
                            ))}
                        </CustomTimeList>

                        {!disabled && (
                            <AddButton onClick={handleAddCustomItem}>
                                <PlusOutlined />
                                {addButtonText}
                            </AddButton>
                        )}
                    </CustomTimeSection>
                )}
            </FeeTypeBody>
        </StyledFeeTypeCard>
    );
};
